// Org-admin users tab — list, create, edit, disable, and enable users within
// the current organisation. The create/edit forms also surface the per-user
// contractAccess scope (all-contracts vs
// selected). On user-create with selected access, a multi-select contract
// picker lets the admin grant initial contracts; each selected contract
// triggers an individual grant call after user creation. The grant loop
// stops on first failure and surfaces granted / failed / remaining lists
// so the admin can recover on the contract-detail Access surface.

import { useEffect, useRef, useState } from 'react';
import { Plus, Pencil, UserX, UserCheck, Mail } from 'lucide-react';
import {
  useOrgUsers,
  useCreateOrgUser,
  useUpdateOrgUser,
  useDisableOrgUser,
  useEnableOrgUser,
  useResendOrgUserInvitation,
} from '../../hooks/useOrgUsers';
import {
  useGrantContractAcl,
  useRevokeContractAcl,
  useUserContractGrants,
} from '../../hooks/useContractAcl';
import { useManagedContracts } from '../../hooks/useManagedContracts';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button, LoadingSpinner, Badge, Alert } from '../../components/common';
import Modal from '../../components/common/Modal';
import { generateTemporaryPassword } from '../../utils/generateTemporaryPassword';
import { isConflictError } from '../../services/api';
import { clsx } from 'clsx';
import AccessAreasFieldset from '../../components/admin/AccessAreasFieldset';
import ContractAccessFieldset from '../../components/admin/ContractAccessFieldset';
import ContractGrantPicker from '../../components/admin/ContractGrantPicker';
import type { OrganisationUser, Area, ContractAccess } from '../../types';

interface InitialGrantResult {
  granted: string[];          // contract titles
  failed: { title: string; message: string } | null;
  remaining: string[];        // contract titles
}

export default function OrgUsersTab() {
  const { user: currentUser } = useAuth();
  const { data, isLoading } = useOrgUsers();
  const createMutation = useCreateOrgUser();
  const updateMutation = useUpdateOrgUser();
  const disableMutation = useDisableOrgUser();
  const enableMutation = useEnableOrgUser();
  const resendMutation = useResendOrgUserInvitation();
  const grantAclMutation = useGrantContractAcl();
  const revokeAclMutation = useRevokeContractAcl();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<OrganisationUser | null>(null);
  const [disableUser, setDisableUser] = useState<OrganisationUser | null>(null);
  const [grantResult, setGrantResult] = useState<InitialGrantResult | null>(null);
  // Transient success/info banner for invitation flows (sent on create,
  // resent on row action) and the 409 "already accepted" outcome from
  // resend. Auto-dismisses after ~4s; the timer ref is held so a quick
  // follow-up resend doesn't get its banner truncated by a stale timer.
  type InvitationBanner =
    | { variant: 'success'; message: string }
    | { variant: 'info'; message: string };
  const [invitationBanner, setInvitationBanner] =
    useState<InvitationBanner | null>(null);
  const invitationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (invitationTimerRef.current !== null) {
        clearTimeout(invitationTimerRef.current);
      }
    };
  }, []);
  const showInvitationBanner = (banner: InvitationBanner): void => {
    if (invitationTimerRef.current !== null) {
      clearTimeout(invitationTimerRef.current);
    }
    setInvitationBanner(banner);
    invitationTimerRef.current = setTimeout(() => {
      setInvitationBanner(null);
      invitationTimerRef.current = null;
    }, 4000);
  };

  const handleCreate = async (input: {
    email: string;
    name?: string;
    role: 'admin' | 'editor' | 'viewer';
    temporaryPassword?: string;
    permissions?: { areas: Area[] };
    contractAccess?: ContractAccess;
    initialGrants?: { id: string; title: string }[];
  }) => {
    const created = await createMutation.mutateAsync({
      email: input.email,
      name: input.name || undefined,
      role: input.role,
      temporaryPassword: input.temporaryPassword || undefined,
      permissions: input.permissions,
      contractAccess: input.contractAccess,
    });

    // Initial-grants fan-out — backend has no batch endpoint, so we issue
    // one POST per selected contract. Stop on first failure (silent
    // partial-success is worse than a clear stop) and surface the lists
    // so the admin can recover via the contract-detail Access tab.
    const initialGrants = input.initialGrants ?? [];
    if (initialGrants.length > 0 && created?.userId) {
      const granted: string[] = [];
      for (let i = 0; i < initialGrants.length; i++) {
        const { id, title } = initialGrants[i];
        try {
          await grantAclMutation.mutateAsync({
            contractId: id,
            userId: created.userId,
          });
          granted.push(title);
        } catch (err) {
          const remaining = initialGrants.slice(i + 1).map((g) => g.title);
          setGrantResult({
            granted,
            failed: {
              title,
              message: err instanceof Error ? err.message : 'Unknown error',
            },
            remaining,
          });
          return;
        }
      }
    }
    setIsCreateOpen(false);
    showInvitationBanner({
      variant: 'success',
      message: `Invitation email sent to ${input.email}.`,
    });
  };

  const handleResend = async (u: OrganisationUser): Promise<void> => {
    try {
      await resendMutation.mutateAsync(u.userId);
      showInvitationBanner({
        variant: 'success',
        message: `Invitation email resent to ${u.email}.`,
      });
    } catch (err) {
      // Conflict (409) means the user has already accepted their
      // invitation — surface as an info banner so the operator knows
      // resend isn't applicable and the user can sign in directly.
      if (isConflictError(err)) {
        showInvitationBanner({
          variant: 'info',
          message: `${u.email} has already accepted their invitation.`,
        });
      } else {
        showInvitationBanner({
          variant: 'info',
          message:
            err instanceof Error
              ? `Failed to resend: ${err.message}`
              : 'Failed to resend invitation.',
        });
      }
    }
  };

  const handleUpdate = async (userId: string, input: {
    name: string;
    role: 'admin' | 'editor' | 'viewer';
    permissions?: { areas: Area[] };
    contractAccess?: ContractAccess;
    grantsAdded?: { id: string; title: string }[];
    grantsRemoved?: { id: string; title: string }[];
  }) => {
    await updateMutation.mutateAsync({
      userId,
      input: {
        name: input.name,
        role: input.role,
        permissions: input.permissions,
        contractAccess: input.contractAccess,
      },
    });

    // Per-contract grant diff fan-out with partial-failure recovery. Each
    // successful call lands; on the first failure we surface what
    // applied, what failed, and what's still queued via the same
    // grantResult modal the create flow uses, so the admin can resume
    // by re-opening the user and saving again (the user-contract-grants
    // query refetches on mutation success, so the diff next time will
    // correctly skip the already-applied calls).
    const grantsAdded = input.grantsAdded ?? [];
    const grantsRemoved = input.grantsRemoved ?? [];
    const granted: string[] = [];
    for (let i = 0; i < grantsAdded.length; i++) {
      const { id, title } = grantsAdded[i];
      try {
        await grantAclMutation.mutateAsync({ contractId: id, userId });
        granted.push(title);
      } catch (err) {
        // Surface progress + the remaining queued items so the admin
        // can recover. Revokes that haven't run yet stay queued too.
        const remainingAdds = grantsAdded.slice(i + 1).map((g) => g.title);
        const remainingRevokes = grantsRemoved.map((g) => `revoke ${g.title}`);
        setGrantResult({
          granted,
          failed: {
            title,
            message: err instanceof Error ? err.message : 'Unknown error',
          },
          remaining: [...remainingAdds, ...remainingRevokes],
        });
        return;
      }
    }
    for (let i = 0; i < grantsRemoved.length; i++) {
      const { id, title } = grantsRemoved[i];
      try {
        await revokeAclMutation.mutateAsync({ contractId: id, userId });
        granted.push(`revoke ${title}`);
      } catch (err) {
        const remainingRevokes = grantsRemoved.slice(i + 1).map((g) => `revoke ${g.title}`);
        setGrantResult({
          granted,
          failed: {
            title: `revoke ${title}`,
            message: err instanceof Error ? err.message : 'Unknown error',
          },
          remaining: remainingRevokes,
        });
        return;
      }
    }
    setEditUser(null);
  };

  const handleDisable = async (userId: string) => {
    await disableMutation.mutateAsync(userId);
    setDisableUser(null);
  };

  const handleEnable = async (userId: string) => {
    await enableMutation.mutateAsync(userId);
  };

  return (
    <>
      {invitationBanner && (
        <div className="mb-4">
          <Alert variant={invitationBanner.variant}>{invitationBanner.message}</Alert>
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button onClick={() => setIsCreateOpen(true)} icon={<Plus className="h-4 w-4" />}>
          Add User
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <Card padding="none">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Role</th>
                <th scope="col" className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Access</th>
                <th scope="col" className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Created</th>
                <th scope="col" className="px-6 py-3 text-right text-overline uppercase text-slate-500 dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {data?.items.map((u) => (
                <tr
                  key={u.userId}
                  className={clsx(
                    u.status === 'disabled' && 'opacity-60'
                  )}
                >
                  <td className="px-6 py-4 text-body-sm text-slate-900 dark:text-slate-50">{u.name}</td>
                  <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">{u.email}</td>
                  <td className="px-6 py-4 text-body-sm text-slate-700 dark:text-slate-300 capitalize">{u.role}</td>
                  <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">
                    {(u.role === 'admin'
                      ? ['generate', 'manage']
                      : u.permissions?.areas || ['generate']
                    ).map((a) => (
                      <Badge key={a} variant={a === 'manage' ? 'pending' : 'active'} label={a} className="mr-1 capitalize" />
                    ))}
                  </td>
                  <td className="px-6 py-4">
                    {u.status === 'disabled' ? (
                      <Badge variant="draft" label="Disabled" />
                    ) : (
                      <Badge variant="active" label="Active" />
                    )}
                  </td>
                  <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <Button
                      variant="secondary"
                      iconOnly
                      size="sm"
                      icon={<Pencil className="h-4 w-4" />}
                      aria-label="Edit user"
                      onClick={() => setEditUser(u)}
                    />
                    {u.userId !== currentUser?.userId && (
                      <Button
                        variant="secondary"
                        iconOnly
                        size="sm"
                        icon={<Mail className="h-4 w-4" />}
                        aria-label="Resend invitation"
                        onClick={() => handleResend(u)}
                        disabled={resendMutation.isPending}
                      />
                    )}
                    {u.status === 'disabled' ? (
                      <Button
                        variant="secondary"
                        iconOnly
                        size="sm"
                        icon={<UserCheck className="h-4 w-4" />}
                        aria-label="Enable user"
                        onClick={() => handleEnable(u.userId)}
                        disabled={enableMutation.isPending}
                      />
                    ) : (
                      u.userId !== currentUser?.userId && (
                        <Button
                          variant="danger"
                          iconOnly
                          size="sm"
                          icon={<UserX className="h-4 w-4" />}
                          aria-label="Disable user"
                          onClick={() => setDisableUser(u)}
                          disabled={disableMutation.isPending}
                        />
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data?.items || data.items.length === 0) && (
            <p className="text-center text-slate-500 dark:text-slate-400 py-8">No users found.</p>
          )}
        </Card>
      )}

      {/* Create user modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Add User">
        <CreateUserForm
          onSubmit={handleCreate}
          isLoading={createMutation.isPending || grantAclMutation.isPending}
        />
      </Modal>

      {/* Initial-grant partial-failure recovery banner */}
      <Modal
        isOpen={!!grantResult}
        onClose={() => setGrantResult(null)}
        title="Contract grants incomplete"
      >
        {grantResult && (
          <div role="alert" className="space-y-3">
            <p className="text-body-sm text-slate-700 dark:text-slate-300">
              The user was saved, but not every contract grant change
              succeeded. Finish the remaining changes from the
              contract-detail Access surface or by re-opening this user.
            </p>
            {grantResult.granted.length > 0 && (
              <p className="text-body-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold">Granted:</span>{' '}
                {grantResult.granted.join(', ')}.
              </p>
            )}
            {grantResult.failed && (
              <p className="text-body-sm text-red-700 dark:text-red-400">
                <span className="font-semibold">Failed:</span>{' '}
                {grantResult.failed.title} — {grantResult.failed.message}.
              </p>
            )}
            {grantResult.remaining.length > 0 && (
              <p className="text-body-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold">Remaining:</span>{' '}
                {grantResult.remaining.join(', ')}.
              </p>
            )}
            <div className="flex justify-end pt-2">
              <Button onClick={() => {
                setGrantResult(null);
                setIsCreateOpen(false);
              }}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit user modal */}
      <Modal
        isOpen={!!editUser}
        onClose={() => setEditUser(null)}
        title="Edit User"
      >
        {editUser && (
          <EditUserForm
            user={editUser}
            onSubmit={(input) => handleUpdate(editUser.userId, input)}
            isLoading={updateMutation.isPending}
          />
        )}
      </Modal>

      {/* Disable confirmation modal */}
      <Modal
        isOpen={!!disableUser}
        onClose={() => setDisableUser(null)}
        title="Disable User"
      >
        {disableUser && (
          <div className="space-y-4">
            <Alert variant="warning">
              <strong>{disableUser.name}</strong> ({disableUser.email}) will no longer be able to log in.
              You can re-enable this user later.
            </Alert>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDisableUser(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDisable(disableUser.userId)}
                loading={disableMutation.isPending}
              >
                Confirm Disable
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function CreateUserForm({
  onSubmit,
  isLoading,
}: {
  onSubmit: (input: {
    email: string;
    name?: string;
    role: 'admin' | 'editor' | 'viewer';
    temporaryPassword?: string;
    permissions?: { areas: Area[] };
    contractAccess?: ContractAccess;
    initialGrants?: { id: string; title: string }[];
  }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  // Generation access defaults to true (most common case) but is now
  // toggleable for non-admins. Backend PermissionsSchema requires at
  // least one area; the form gates submission when both are off.
  const [generateAccess, setGenerateAccess] = useState(true);
  const [manageAccess, setManageAccess] = useState(false);
  const [password, setPassword] = useState('');
  // ACL controls — defaults match the backend mapper defaults so the
  // form-state-to-payload contract is symmetric with read.
  const [contractAccess, setContractAccess] = useState<ContractAccess>('all');
  const [selectedContractIds, setSelectedContractIds] = useState<string[]>([]);

  const isAdmin = role === 'admin';
  // Admins are server-enforced to all-contracts + both areas; the UI
  // mirrors that lock by disabling the controls and overriding the
  // values sent to the API.
  const effectiveGenerateAccess: boolean = isAdmin ? true : generateAccess;
  const effectiveManageAccess: boolean = isAdmin ? true : manageAccess;
  const effectiveContractAccess: ContractAccess = isAdmin ? 'all' : contractAccess;
  const areasEmpty = !effectiveGenerateAccess && !effectiveManageAccess;

  const managedContractsQuery = useManagedContracts();
  const managedContracts = managedContractsQuery.data?.items ?? [];

  const generatePassword = () => setPassword(generateTemporaryPassword());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Build the areas array from the two toggles in stable order. Admin
    // override is applied via effective* values above. Backend schema
    // requires at least one area; the submit button is disabled when
    // both are off so this can never reach the API empty.
    const areas: Area[] = [
      ...(effectiveGenerateAccess ? (['generate'] as const) : []),
      ...(effectiveManageAccess ? (['manage'] as const) : []),
    ];
    const initialGrants =
      effectiveManageAccess && effectiveContractAccess === 'selected'
        ? managedContracts
            .filter((c) => selectedContractIds.includes(c.id))
            .map((c) => ({ id: c.id, title: c.title }))
        : undefined;
    onSubmit({
      email,
      name: name.trim() || undefined,
      role,
      temporaryPassword: password || undefined,
      permissions: { areas },
      contractAccess: effectiveContractAccess,
      initialGrants,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="create-name" className="label">Name</label>
        <input
          id="create-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          placeholder="Optional — defaults to email"
        />
      </div>
      <div>
        <label htmlFor="create-email" className="label">Email *</label>
        <input
          id="create-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
        />
      </div>
      <div>
        <label htmlFor="create-role" className="label">Role *</label>
        <select
          id="create-role"
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'editor' | 'viewer')}
          className="input"
        >
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>
      <AccessAreasFieldset
        isAdmin={isAdmin}
        generateAccess={effectiveGenerateAccess}
        manageAccess={effectiveManageAccess}
        onGenerateAccessChange={setGenerateAccess}
        onManageAccessChange={setManageAccess}
        areasEmpty={areasEmpty}
      />
      {effectiveManageAccess && (
        <>
          {/* Contract access scoping only matters for users who can manage
              contracts at all — hide the fieldset entirely when manage is
              off so the picker can't be set on a user who can never reach
              the Contracts surface. */}
          <ContractAccessFieldset
            name="create-contract-access"
            isAdmin={isAdmin}
            contractAccess={effectiveContractAccess}
            onChange={setContractAccess}
          />
          {effectiveContractAccess === 'selected' && !isAdmin && (
            <ContractGrantPicker
              legend="Initial contract grants"
              description="Tick the contracts this user should see on creation. Add or remove grants later from the contract-detail Access tab."
              contracts={managedContracts}
              selectedIds={selectedContractIds}
              onToggle={(id, checked) => {
                if (checked) {
                  setSelectedContractIds([...selectedContractIds, id]);
                } else {
                  setSelectedContractIds(
                    selectedContractIds.filter((existingId) => existingId !== id),
                  );
                }
              }}
              emptyMessage="No contracts available to grant."
            />
          )}
        </>
      )}
      <div>
        <label htmlFor="create-password" className="label">Temporary Password</label>
        <div className="flex gap-2">
          <input
            id="create-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Auto-generated if empty"
            className="input flex-1"
          />
          <Button variant="secondary" size="sm" type="button" onClick={generatePassword}>
            Generate
          </Button>
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={!email || areasEmpty} loading={isLoading}>
          Create User
        </Button>
      </div>
    </form>
  );
}

function EditUserForm({
  user,
  onSubmit,
  isLoading,
}: {
  user: OrganisationUser;
  onSubmit: (input: {
    name: string;
    role: 'admin' | 'editor' | 'viewer';
    permissions?: { areas: Area[] };
    contractAccess?: ContractAccess;
    grantsAdded?: { id: string; title: string }[];
    grantsRemoved?: { id: string; title: string }[];
  }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>(
    user.role as 'admin' | 'editor' | 'viewer'
  );
  // Hydrate from stored permissions; the platform default for legacy rows
  // (no permissions record) is generate-only.
  const [generateAccess, setGenerateAccess] = useState(
    user.permissions?.areas?.includes('generate') ?? true,
  );
  const [manageAccess, setManageAccess] = useState(
    user.permissions?.areas?.includes('manage') ?? false
  );
  const [contractAccess, setContractAccess] = useState<ContractAccess>(
    user.contractAccess ?? 'all',
  );
  // Pending edits to the user's per-contract grants. Hydrated from the
  // user-contract-grants endpoint on mount; flushed as diff-against-original
  // on save (POST for added, DELETE for removed).
  const [grantedContractIds, setGrantedContractIds] = useState<string[]>([]);
  const [originalGrantedContractIds, setOriginalGrantedContractIds] = useState<
    string[]
  >([]);

  const isAdmin = role === 'admin';
  const effectiveGenerateAccess: boolean = isAdmin ? true : generateAccess;
  const effectiveManageAccess: boolean = isAdmin ? true : manageAccess;
  const effectiveContractAccess: ContractAccess = isAdmin ? 'all' : contractAccess;
  const areasEmpty = !effectiveGenerateAccess && !effectiveManageAccess;

  const managedContractsQuery = useManagedContracts();
  const managedContracts = managedContractsQuery.data?.items ?? [];

  // Hydrate the user's existing contract grants once so the picker shows
  // current state and on save we can compute the diff. Only useful when
  // the user has scoped access — otherwise the grant list is conceptually
  // empty (admins/'all' users have implicit access).
  const userGrantsQuery = useUserContractGrants(
    effectiveContractAccess === 'selected' && !isAdmin
      ? user.userId
      : undefined,
  );
  // Pattern 0: one-shot hydration guard — seed the granted-contract lists
  // exactly once when the query data first arrives. Boolean state replaces
  // the ref so the update runs during render (before commit) with no effect.
  const [grantedHydrated, setGrantedHydrated] = useState(false);
  const grantQueryItems = userGrantsQuery.data?.items;
  if (!grantedHydrated && grantQueryItems) {
    setGrantedHydrated(true);
    const ids = grantQueryItems.map((g) => g.contractId);
    setGrantedContractIds(ids);
    setOriginalGrantedContractIds(ids);
  }

  const originalGenerateAccess = user.permissions?.areas?.includes('generate') ?? true;
  const originalManageAccess = user.permissions?.areas?.includes('manage') ?? false;
  const originalContractAccess = user.contractAccess ?? 'all';
  const aclChanged =
    effectiveContractAccess !== originalContractAccess;
  const grantsChanged =
    effectiveContractAccess === 'selected' &&
    (grantedContractIds.length !== originalGrantedContractIds.length ||
      grantedContractIds.some((id) => !originalGrantedContractIds.includes(id)));
  const isUnchanged =
    name === user.name &&
    role === user.role &&
    generateAccess === originalGenerateAccess &&
    manageAccess === originalManageAccess &&
    !aclChanged &&
    !grantsChanged;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const areas: Area[] = [
      ...(effectiveGenerateAccess ? (['generate'] as const) : []),
      ...(effectiveManageAccess ? (['manage'] as const) : []),
    ];
    // Compute grant diff against the original. Caller handles the
    // POST/DELETE fan-out; the form provides {id, title} pairs so the
    // caller's progress/recovery surface can render meaningful labels
    // (the user-contract-grants hydration carries the original titles;
    // the managed-contracts list covers the newly-added ids).
    const titleForId = (id: string): string => {
      const hydrated = userGrantsQuery.data?.items.find((g) => g.contractId === id);
      if (hydrated) return `${hydrated.contractNumber} — ${hydrated.title}`;
      const mc = managedContracts.find((c) => c.id === id);
      if (mc) return `${mc.contractNumber} — ${mc.title}`;
      return id;
    };
    const added =
      effectiveContractAccess === 'selected' && !isAdmin
        ? grantedContractIds
            .filter((id) => !originalGrantedContractIds.includes(id))
            .map((id) => ({ id, title: titleForId(id) }))
        : [];
    const removed =
      effectiveContractAccess === 'selected' && !isAdmin
        ? originalGrantedContractIds
            .filter((id) => !grantedContractIds.includes(id))
            .map((id) => ({ id, title: titleForId(id) }))
        : [];
    onSubmit({
      name,
      role,
      permissions: { areas },
      contractAccess: effectiveContractAccess,
      grantsAdded: added.length > 0 ? added : undefined,
      grantsRemoved: removed.length > 0 ? removed : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="edit-name" className="label">Name</label>
        <input
          id="edit-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
        />
      </div>
      <div>
        <label htmlFor="edit-role" className="label">Role</label>
        <select
          id="edit-role"
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'editor' | 'viewer')}
          className="input"
        >
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>
      <AccessAreasFieldset
        isAdmin={isAdmin}
        generateAccess={effectiveGenerateAccess}
        manageAccess={effectiveManageAccess}
        onGenerateAccessChange={setGenerateAccess}
        onManageAccessChange={setManageAccess}
        areasEmpty={areasEmpty}
      />
      {effectiveManageAccess && (
        <>
          {/* Contract access scoping only matters for users who can manage
              contracts at all — hide entirely when manage is off. */}
          <ContractAccessFieldset
            name="edit-contract-access"
            isAdmin={isAdmin}
            contractAccess={effectiveContractAccess}
            onChange={setContractAccess}
          />
          {!isAdmin && effectiveContractAccess === 'selected' && (
            <ContractGrantPicker
              legend="Contract grants"
              description={`Tick the contracts this user should see. Changes apply on Save. You can also manage grants from each contract’s Access & People tab.`}
              contracts={managedContracts}
              selectedIds={grantedContractIds}
              onToggle={(id, checked) => {
                if (checked) {
                  setGrantedContractIds([...grantedContractIds, id]);
                } else {
                  setGrantedContractIds(
                    grantedContractIds.filter((existingId) => existingId !== id),
                  );
                }
              }}
              emptyMessage="No contracts available."
            />
          )}
        </>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="submit" disabled={isUnchanged || areasEmpty} loading={isLoading}>
          Save Changes
        </Button>
      </div>
    </form>
  );
}
